import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, FileText, HelpCircle, X } from "lucide-react";
import { TrainingTour } from "../tour/TrainingTour";
import { useTrainingTour } from "../tour/useTrainingTour";
import { flowDetailTourSteps } from "../tour/trainingTourSteps";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Background,
  Controls,
  type Edge,
  MiniMap,
  type Node,
  type NodeMouseHandler,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from "reactflow";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { RichTextOrPlain } from "@/shared/ui/RichTextOrPlain";
import { useFlowDetailQuery } from "../api/queries";
import { trainingRoutes } from "../trainingRoutes";
import { TrainingFlowStepNode } from "../ui/TrainingFlowStepNode";
import "reactflow/dist/style.css";

const defaultFlowsListSearch = {
  sortBy: undefined as string | undefined,
  sortOrder: undefined as "asc" | "desc" | undefined,
};

const defaultChecklistsListSearch = {
  sortBy: undefined as string | undefined,
  sortOrder: undefined as "asc" | "desc" | undefined,
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ຮ່າງ",
  inactive: "ບໍ່ໃຊ້ງານ",
};

const nodeTypes = {
  flowStep: TrainingFlowStepNode,
};

function getHandlePositions(
  sourceNode: Node | undefined,
  targetNode: Node | undefined,
): { sourceHandle: string; targetHandle: string } {
  if (!sourceNode || !targetNode) {
    return { sourceHandle: "right", targetHandle: "left" };
  }
  const sourceX = sourceNode.position.x;
  const sourceY = sourceNode.position.y;
  const targetX = targetNode.position.x;
  const targetY = targetNode.position.y;
  const deltaX = Math.abs(targetX - sourceX);
  const deltaY = Math.abs(targetY - sourceY);
  if (deltaY > deltaX) {
    if (targetY > sourceY) {
      return { sourceHandle: "bottom", targetHandle: "top" };
    }
    return { sourceHandle: "top", targetHandle: "bottom" };
  }
  if (targetX > sourceX) {
    return { sourceHandle: "right", targetHandle: "left" };
  }
  return { sourceHandle: "left", targetHandle: "right" };
}

type FlowStepRow = {
  id: string;
  stepNumber: number;
  positionX: number | null;
  positionY: number | null;
  title: string;
  description: string | null;
  notes: string | null;
  responsiblePositionId: string | null;
  stepTypeName: string | null;
  positionName: string | null;
  departmentName: string | null;
};

type FlowConnectionRow = {
  id: string;
  fromStepId: string;
  toStepId: string;
  label: string | null;
};

type FlowMappingRow = {
  id: string;
  sopId: string | null;
  sopTitle: string | null;
  departmentId: string | null;
  departmentName: string | null;
  positionId: string | null;
  positionName: string | null;
};

type FlowDetailPayload = {
  title: string;
  description: string | null;
  purpose: string | null;
  status: string;
  departmentName: string | null;
  steps: FlowStepRow[];
  connections: FlowConnectionRow[];
  mappings: FlowMappingRow[];
};

export function FlowDetailPage() {
  const tour = useTrainingTour("training-flow-detail-tour-completed");
  const navigate = useNavigate({ from: "/app/training/flows/$id" });
  const { id } = useParams({ from: "/app/training/flows/$id" });
  const { data, isLoading } = useFlowDetailQuery(id);
  const flow = data as FlowDetailPayload | undefined;

  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  const goToList = useCallback(() => {
    navigate({
      to: trainingRoutes.flowsList,
      search: defaultFlowsListSearch,
    });
  }, [navigate]);

  const initialNodes = useMemo<Node[]>(() => {
    if (!flow?.steps?.length) return [];
    return flow.steps.map((step) => {
      const px = step.positionX != null && step.positionX !== 0 ? step.positionX : null;
      const py = step.positionY != null && step.positionY !== 0 ? step.positionY : null;
      const x = px ?? step.stepNumber * 250;
      const y = py ?? 100;
      return {
        id: step.id,
        type: "flowStep",
        position: { x, y },
        data: { step },
        draggable: false,
      };
    });
  }, [flow?.steps]);

  const initialEdges = useMemo<Edge[]>(() => {
    if (!flow?.connections?.length || !initialNodes.length) return [];
    return flow.connections.map((connection) => {
      const sourceNode = initialNodes.find((n) => n.id === connection.fromStepId);
      const targetNode = initialNodes.find((n) => n.id === connection.toStepId);
      const handles = getHandlePositions(sourceNode, targetNode);
      return {
        id: connection.id,
        source: connection.fromStepId,
        target: connection.toStepId,
        sourceHandle: handles.sourceHandle,
        targetHandle: handles.targetHandle,
        label: connection.label || undefined,
        type: "smoothstep",
        animated: false,
        style: { stroke: "#3b82f6", strokeWidth: 3 },
      };
    });
  }, [flow?.connections, initialNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  const selectedStep = useMemo(() => {
    if (!selectedStepId || !flow?.steps) return null;
    return flow.steps.find((s) => s.id === selectedStepId) ?? null;
  }, [selectedStepId, flow?.steps]);

  const firstLinkedSopId = useMemo(() => {
    const m = flow?.mappings?.find((x) => x.sopId);
    return m?.sopId ?? null;
  }, [flow?.mappings]);

  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    setSelectedStepId(node.id);
  }, []);

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກຳລັງໂຫລດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (!flow) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
            <p className="text-muted-foreground">ບໍ່ພົບ Flow</p>
            <Button type="button" variant="outline" className="gap-2" onClick={goToList}>
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-4">
          <div
            className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
            data-tourid="training-flow-detail-title"
          >
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-2xl">{flow.title}</h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0"
                  onClick={tour.startTour}
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-4" />
                </Button>
              </div>
              {flow.description ? (
                <p className="mt-1 text-muted-foreground text-sm">{flow.description}</p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              className="w-full shrink-0 gap-2 sm:w-auto"
              onClick={goToList}
            >
              <ArrowLeft className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {!selectedStepId ? (
              <div className="space-y-4 lg:col-span-1">
                <div className="space-y-4 rounded-xl border bg-card p-6">
                  <h2 className="font-semibold text-lg">ລາຍລະອຽດ Flow</h2>
                  <div className="space-y-4">
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">ຊື່ Flow</div>
                      <p className="mt-1">{flow.title}</p>
                    </div>
                    {flow.departmentName ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">ພະແນກ</div>
                        <p className="mt-1">{flow.departmentName}</p>
                      </div>
                    ) : null}
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">ສະຖານະ</div>
                      <p className="mt-1">
                        {statusLabels[flow.status] ?? flow.status}
                      </p>
                    </div>
                    {flow.purpose ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຈຸດປະສົງ
                        </div>
                        <p className="mt-1">{flow.purpose}</p>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-4 rounded-xl border bg-card p-6">
                  <h2 className="font-semibold text-lg">ການຜູກກັບ</h2>
                  <div className="space-y-3">
                    {flow.mappings?.length ? (
                      flow.mappings.map((mapping) => (
                        <div
                          key={mapping.id}
                          className="flex items-start justify-between rounded-lg border p-3"
                        >
                          <div className="flex-1 space-y-2">
                            {mapping.sopTitle ? (
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-muted-foreground text-sm">
                                  SOP:
                                </span>
                                <span className="text-sm">{mapping.sopTitle}</span>
                                {mapping.sopId ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    aria-label="ເປີດ SOP"
                                    onClick={() =>
                                      navigate({
                                        from: "/app/training/flows/$id",
                                        to: trainingRoutes.sopDetail,
                                        params: { id: mapping.sopId as string },
                                      })
                                    }
                                  >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </Button>
                                ) : null}
                              </div>
                            ) : null}
                            {mapping.departmentName ? (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-muted-foreground text-sm">
                                  ພະແນກ:
                                </span>
                                <span>{mapping.departmentName}</span>
                              </div>
                            ) : null}
                            {mapping.positionName ? (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-medium text-muted-foreground text-sm">
                                  ຕໍາແໜ່ງ:
                                </span>
                                <span>{mapping.positionName}</span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີການຜູກກັບ</p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="lg:col-span-2">
              <div className="rounded-xl border bg-card" data-tourid="training-flow-detail-canvas">
                <div className="border-b p-4">
                  <h2 className="font-semibold text-lg">ຜະແນກຜັງ Flow</h2>
                  {flow.steps.length === 0 ? (
                    <p className="mt-1 text-muted-foreground text-sm">
                      ຍັງບໍ່ມີຂັ້ນຕອນໃນ Flow ນີ້
                    </p>
                  ) : null}
                </div>

                {flow.steps.length > 0 ? (
                  <div className="h-[600px] w-full">
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onNodeClick={onNodeClick}
                      nodeTypes={nodeTypes}
                      fitView
                      className="bg-background"
                      nodesDraggable={false}
                      nodesConnectable={false}
                      elementsSelectable
                      connectionLineStyle={{ stroke: "#3b82f6", strokeWidth: 3 }}
                      defaultEdgeOptions={{
                        type: "smoothstep",
                        style: { stroke: "#3b82f6", strokeWidth: 3 },
                      }}
                    >
                      <Controls className="border bg-background" />
                      <Background color="hsl(var(--muted))" gap={20} />
                      <MiniMap
                        className="border bg-background"
                        nodeColor={(node) => {
                          const stepType = (node.data as { step?: { stepTypeName?: string } })
                            ?.step?.stepTypeName;
                          if (
                            stepType?.includes("ວຽກ") ||
                            stepType?.toLowerCase().includes("work")
                          ) {
                            return "hsl(217, 91%, 60%)";
                          }
                          if (
                            stepType?.includes("ກວດສອບ") ||
                            stepType?.toLowerCase().includes("check")
                          ) {
                            return "hsl(43, 96%, 56%)";
                          }
                          if (
                            stepType?.includes("ອະນຸມັດ") ||
                            stepType?.toLowerCase().includes("approve")
                          ) {
                            return "hsl(142, 76%, 36%)";
                          }
                          return "hsl(var(--muted-foreground))";
                        }}
                      />
                    </ReactFlow>
                  </div>
                ) : (
                  <div className="flex h-[400px] items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <p className="mb-2">ບໍ່ມີຂັ້ນຕອນ</p>
                      <p className="text-sm">ຍັງບໍ່ມີຂັ້ນຕອນໃນ Flow ນີ້</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {selectedStepId && selectedStep ? (
              <div className="lg:col-span-1">
                <div className="rounded-xl border bg-card">
                  <div className="flex items-center justify-between border-b p-4">
                    <h2 className="font-semibold text-lg">ລາຍລະອຽດຂັ້ນຕອນ</h2>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="ປິດ"
                      onClick={() => setSelectedStepId(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-4 p-4">
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">ຊື່ຂັ້ນຕອນ</div>
                      <p className="mt-1 font-semibold">{selectedStep.title}</p>
                    </div>
                    <div>
                      <div className="font-medium text-muted-foreground text-sm">ລຳດັບ</div>
                      <p className="mt-1">#{selectedStep.stepNumber}</p>
                    </div>
                    {selectedStep.stepTypeName ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">ປະເພດ</div>
                        <p className="mt-1">{selectedStep.stepTypeName}</p>
                      </div>
                    ) : null}
                    {selectedStep.description ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຄຳອະທິບາຍ
                        </div>
                        <div className="mt-1 text-sm">
                          <RichTextOrPlain text={selectedStep.description} />
                        </div>
                      </div>
                    ) : null}
                    {selectedStep.positionName ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">
                          ຜູ້ຮັບຜິດຊອບ
                        </div>
                        <p className="mt-1">{selectedStep.positionName}</p>
                      </div>
                    ) : null}
                    {selectedStep.departmentName ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">ພະແນກ</div>
                        <p className="mt-1">{selectedStep.departmentName}</p>
                      </div>
                    ) : null}
                    {selectedStep.notes ? (
                      <div>
                        <div className="font-medium text-muted-foreground text-sm">ໝາຍເຫດ</div>
                        <p className="mt-1 whitespace-pre-wrap text-sm">{selectedStep.notes}</p>
                      </div>
                    ) : null}

                    <div className="space-y-2 border-t pt-4">
                      {firstLinkedSopId ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full gap-2"
                          onClick={() =>
                            navigate({
                              from: "/app/training/flows/$id",
                              to: trainingRoutes.sopDetail,
                              params: { id: firstLinkedSopId },
                            })
                          }
                        >
                          <FileText className="h-4 w-4" />
                          ເປີດ SOP ທີ່ກ່ຽວຂ້ອງ
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() =>
                          navigate({
                            from: "/app/training/flows/$id",
                            to: trainingRoutes.checklistsList,
                            search: defaultChecklistsListSearch,
                          })
                        }
                      >
                        <FileText className="h-4 w-4" />
                        ເບິ່ງ Checklist
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <TrainingTour steps={flowDetailTourSteps} run={tour.run} onCallback={tour.handleJoyrideCallback} />
      </Main>
    </>
  );
}
